import React from 'react';
import { graphql, useLazyLoadQuery, useFragment } from 'react-relay';

import Skeleton from '@atlaskit/skeleton';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { VersionsEditWrapperQuery } from '@atlassian/jira-relay/src/__generated__/VersionsEditWrapperQuery.graphql';
import type { VersionsEditWrapper$key } from '@atlassian/jira-relay/src/__generated__/VersionsEditWrapper.graphql';
import type { VersionOption } from '@atlassian/jira-issue-field-versions/src/common/types.tsx';
import VersionsEdit from '@atlassian/jira-issue-field-versions/src/ui/edit/main.tsx';
import Placeholder from '@atlassian/jira-placeholder';
import messages from './messages.tsx';

interface VersionsEditWrapperProps {
	options: VersionOption[];
	value: VersionOption[] | null;
	isDropdownMenuFixedAndLayered: boolean;
	onChange: (versionEditValue: VersionOption | VersionOption[] | null) => void;
	error?: string | boolean;
	fieldId: string;
	onCloseMenuOnScroll: (e: Event) => boolean;
	isReadOnly: boolean;
	isDisabled: boolean;
	autoFocus: boolean;
	inputId?: string;
	isRequired?: boolean;
	projectId: string;
	shouldShowCreateVersion: boolean;
	projectKey: string | undefined;
}

export default function VersionsEditWrapper(props: VersionsEditWrapperProps) {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const {
		options,
		value,
		isDropdownMenuFixedAndLayered,
		onChange,
		error,
		fieldId,
		onCloseMenuOnScroll,
		isReadOnly,
		isDisabled,
		autoFocus,
		inputId,
		isRequired,
		projectId,
		shouldShowCreateVersion,
		projectKey,
	} = props;

	const renderVersionsEdit = ({ canManageVersions }: { canManageVersions: boolean }) => (
		<VersionsEdit
			options={options}
			value={value}
			isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
			isMulti
			onChange={onChange}
			isInvalid={Boolean(error)}
			fieldId={fieldId}
			onCloseMenuOnScroll={onCloseMenuOnScroll}
			isDisabled={isReadOnly || isDisabled}
			autoFocus={autoFocus}
			placeholder={isDisabled ? formatMessage(messages.none) : ''}
			inputId={inputId}
			isRequired={isRequired}
			projectId={projectId}
			shouldShowCreateVersion={
				shouldShowCreateVersion || (canManageVersions && fieldId === 'fixVersions')
			}
		/>
	);

	// Conditional rendering is the safest way to conditionally render a Fragment,
	// safer than @include with runtime conditionals (other than feature gates).
	return projectKey ? (
		<Placeholder
			name="multi-versions-field"
			fallback={<Skeleton isShimmering width="100%" height="40px" />}
		>
			<WithCanManageVersions cloudId={cloudId} projectKey={projectKey}>
				{renderVersionsEdit}
			</WithCanManageVersions>
		</Placeholder>
	) : (
		renderVersionsEdit({ canManageVersions: false })
	);
}

function WithCanManageVersions(props: {
	cloudId: string;
	projectKey: string;
	children: ({ canManageVersions }: { canManageVersions: boolean }) => React.ReactElement;
}): React.ReactElement {
	const { cloudId, projectKey } = props;

	// Use preloaded query to get project permissions
	const queryData = useLazyLoadQuery<VersionsEditWrapperQuery>(
		graphql`
			query VersionsEditWrapperQuery($cloudId: ID!, $projectKey: String!) {
				...VersionsEditWrapper @arguments(cloudId: $cloudId, projectKey: $projectKey)
			}
		`,
		{
			cloudId,
			projectKey: projectKey ?? '',
		},
	);

	// Use fragment to read permissions data
	const projectPermissions = useFragment<VersionsEditWrapper$key>(
		graphql`
			fragment VersionsEditWrapper on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, projectKey: { type: "String!" }) {
				jira {
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId) {
						canEditVersions: action(type: MANAGE_VERSIONS) {
							canPerform
						}
					}
				}
			}
		`,
		queryData,
	);

	const canManageVersions =
		!!projectPermissions?.jira?.jiraProjectByKey?.canEditVersions?.canPerform;

	return props.children({ canManageVersions });
}
